/**
 * Raw Fetch RPC Bypass
 * 
 * Bypasses the Supabase JS client for public RPC calls.
 * This provides instant, deadlock-free data fetching that works
 * reliably in both normal and incognito modes.
 * 
 * Used for: get_profile, get_broker_listings_public
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Call a Supabase RPC function via raw fetch
 * Fast and reliable in all browser modes
 */
export const callRpcViaFetch = async <T = any>(
  functionName: string, 
  params: Record<string, any>
): Promise<{ data: T | null; error: Error | null }> => {
  const url = `${supabaseUrl}/rest/v1/rpc/${functionName}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseKey!,
        'Authorization': `Bearer ${supabaseKey!}`
      },
      body: JSON.stringify(params)
    });

    if (!response.ok) {
      const text = await response.text();
      return { data: null, error: new Error(`RPC error: ${response.status} ${text}`) };
    }
    
    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
};
